import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { SetURLSearchParams } from 'react-router-dom';
import { SalvProgressCircle, SalvTable, SalvTablePaginationType } from '@salvapp/ui-components';
import {
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiEntityType,
  InternalApiPerson,
  InternalApiPersonsPage,
  InternalApiSortDirection,
} from '../api-internal';
import { createColumnHelper, PaginationState, Row, SortingState } from '@tanstack/react-table';
import { PersonSearchQuery } from './PersonSearch';
import { getDisplayValue, isChildField, isComplexField } from '../schema/helper';
import ComplexValue from '../aml/sections/attributes/ComplexValue';
import { getPersonPath } from '../navigation/helper';
import { useQueryEntitySchema } from '../schema/entitySchemaActions';
import { SearchParamKeys } from '../common/searchParamKeys';
import { Updater } from '@tanstack/table-core/src/types';
import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InternalLink from '../common/InternalLink';
import { WarnTooltip } from '../common/tooltips/WarnTooltip';
import { getSchemaFieldsOfSearchResults, isSortable } from './helper';
import { getOrderByAndDirection } from '../common/sort';

interface PersonSearchResultsProps {
  personsPage: InternalApiPersonsPage;
  isPersonSearchFetching: boolean;
  submittedSearchState: PersonSearchQuery;
  setSubmittedSearch: Dispatch<SetStateAction<PersonSearchQuery>>;
  setSearchParams: SetURLSearchParams;
  selectedRows?: InternalApiPerson[];
  setSelectedRows?: Dispatch<SetStateAction<InternalApiPerson[]>>;
  isRowSelectionEnabled?: (row: Row<InternalApiPerson>) => boolean;
}

const PersonSearchResults = ({
  personsPage,
  isPersonSearchFetching,
  submittedSearchState,
  setSubmittedSearch,
  setSearchParams,
  selectedRows,
  setSelectedRows,
  isRowSelectionEnabled,
}: PersonSearchResultsProps) => {
  const columnHelper = createColumnHelper<InternalApiPerson>();
  const getColumns = useCallback(
    (schemaFields: InternalApiEntitySchemaField[]) => {
      const { schemaFieldsOfSearchResults, fieldNamesWithMissingSchema } = getSchemaFieldsOfSearchResults(
        personsPage,
        schemaFields
      );
      return [
        columnHelper.display({
          id: 'personLink',
          header: () =>
            fieldNamesWithMissingSchema.length ? (
              <WarnTooltip
                tooltip={`Fields ${fieldNamesWithMissingSchema} exist in Search results but are not specified in your Schema. You should stop seeing this warning when Person re-indexing has finished.`}
              />
            ) : undefined,
          cell: ({ row }) => {
            const person = row.original;
            return (
              <InternalLink
                title={<FontAwesomeIcon icon={faFileLines} style={{ fontSize: 16 }} />}
                link={getPersonPath(person.id)}
                ariaLabel={'Link for Person ' + person.id}
                onClickOpenInNewTab={false}
              />
            );
          },
        }),
        ...schemaFieldsOfSearchResults.map((schemaField) => {
          return columnHelper.accessor('attributes', {
            id: schemaField.fieldName,
            header: schemaField.displayName,
            cell: ({ getValue }) => {
              const value = getValue()![schemaField.fieldName];
              return isComplexField(schemaField) ? (
                <ComplexValue
                  fieldName={schemaField.fieldName}
                  childAttributesAsString={value}
                  parentSchemaFields={schemaFields.filter((field) => !isChildField(field))}
                  schemaFields={schemaFields}
                  isAmountOfValuesLimited={true}
                />
              ) : schemaField.type === InternalApiEntitySchemaFieldType.DATETIME ? (
                <span style={{ whiteSpace: 'nowrap' }}>{getDisplayValue(schemaField, value)}</span>
              ) : (
                getDisplayValue(schemaField, value)
              );
            },
            enableSorting: isSortable(schemaField),
            // TODO give more room for complex fields
            // size: isComplexField(schemaField) ? 200 : 0,
          });
        }),
        columnHelper.accessor('id', {
          id: 'id',
          header: 'ID',
          cell: ({ getValue }) => getValue(),
        }),
      ];
    },
    [personsPage]
  );

  const {
    data: schema,
    isPending: isSchemaPending,
    isError: isSchemaError,
  } = useQueryEntitySchema(InternalApiEntityType.PERSON);

  if (isSchemaError) {
    return <DataLoadingErrorFallback message={'Could not load Schema'} />;
  } else if (isSchemaPending) {
    return <SalvProgressCircle ariaLabel={'Loading Schema'} />;
  }

  const setPagination = (state: PaginationState) => {
    setSubmittedSearch((prev) => {
      return {
        ...prev,
        page: state.pageIndex,
      };
    });
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set(SearchParamKeys.page, state.pageIndex.toString());

      return newParams;
    });
  };

  const sortingState: SortingState = [
    {
      id: submittedSearchState.orderBy || '',
      desc: submittedSearchState.direction === InternalApiSortDirection.DESC,
    },
  ];

  const setSorting = (updaterOrValue: Updater<SortingState>) => {
    const { orderBy, direction } = getOrderByAndDirection(updaterOrValue, sortingState);

    setSubmittedSearch((prev) => {
      return {
        ...prev,
        orderBy,
        direction,
      };
    });
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (orderBy && direction) {
        newParams.set(SearchParamKeys.orderBy, orderBy);
        newParams.set(SearchParamKeys.direction, direction);
      }
      return newParams;
    });
  };

  return (
    <SalvTable
      isLoading={isPersonSearchFetching}
      data={personsPage.content}
      columns={getColumns(schema.schemaFields)}
      pagination={{
        type: SalvTablePaginationType.PAGINATION,
        total: personsPage.total,
        state: {
          pageIndex: personsPage.number,
          pageSize: personsPage.size,
        },
        setPagination,
      }}
      sorting={{
        state: sortingState,
        setSorting: setSorting,
      }}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      isRowSelectionEnabled={isRowSelectionEnabled}
      emptyMessage='No persons found'
      className={'m-b-64'}
    />
  );
};

export default PersonSearchResults;
